import {Injectable} from '@angular/core';
import {FormSnapshot} from '../models/form-config.model';

/**
 * FormDraftService — gestione del salvataggio locale dei form.
 *
 * Usa sessionStorage come storage di default.
 * Per sostituirlo con una chiamata API basta modificare solo questo servizio,
 * senza toccare DynamicFormService o i componenti.
 *
 * providedIn: 'root' → singleton condiviso da tutti i form dell'applicazione.
 */
@Injectable({providedIn: 'root'})
export class FormDraftService {

  /**
   * Salva il valore corrente del form nel sessionStorage.
   * @param formId  ID univoco del form (es. 'anagrafica-cliente')
   * @param value   Snapshot del form da persistere
   */
  saveDraft(formId: string, value: FormSnapshot): void {
    try {
      sessionStorage.setItem(this.storageKey(formId), JSON.stringify(value));
    } catch (e) {
      // QuotaExceededError o contesto senza sessionStorage (SSR)
      console.warn(`[FormDraftService] Impossibile salvare il draft per "${formId}":`, e);
    }
  }

  /**
   * Carica il draft salvato per il form indicato.
   * @returns Lo snapshot salvato, o null se non presente o non parsabile.
   */
  loadDraft(formId: string): FormSnapshot | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(formId));
      if (!raw) return null;
      return JSON.parse(raw) as FormSnapshot;
    } catch {
      return null;
    }
  }

  /**
   * Rimuove il draft dal sessionStorage.
   * Da chiamare dopo un submit andato a buon fine.
   */
  clearDraft(formId: string): void {
    try {
      sessionStorage.removeItem(this.storageKey(formId));
    } catch {
      // ignorato
    }
  }

  /** True se esiste un draft salvato per il form indicato. */
  hasDraft(formId: string): boolean {
    try {
      return sessionStorage.getItem(this.storageKey(formId)) !== null;
    } catch {
      return false;
    }
  }

  private storageKey(formId: string): string {
    return `__df_draft__${formId}`;
  }
}
